import { createMemoryHistory, createRouter, createWebHistory } from "vue-router";

import NProgress from "nprogress/nprogress.js";

// Main layouts
import LayoutActiveWebsite from "@/layouts/variations/ActiveWebsite.vue";
import LayoutSimple from "@/layouts/variations/Simple.vue";
import MCPLayout from "@/layouts/MCPLayout.vue";
import { previewRoutes } from "@/router/preview";

// Frontend: Landing
import LandingHome from "@/views/starter/LandingViewHome.vue";
import LandingPreview from "@/views/starter/LandingPreview.vue";
import LayoutLanding from "@/layouts/LandingPageLayout.vue";
import Preview from "@/views/preview/Preview.vue";
import PreviewOverviewMCP from "@/views/preview/OverviewMCP.vue";

const BackendDashboard = () => import("@/views/backend/pages/Dashboard.vue");
const BackendSpelling = () => import("@/views/backend/pages/Spelling.vue");
const BackendLogs = () => import("@/views/backend/pages/Logs.vue");
const BackendWebsite = () => import("@/views/backend/pages/Website.vue");
const BackendUrlGroup = () => import("@/views/backend/pages/UrlGroup.vue");
const BackendPagesGenericPricingPlans = () => import("@/views/pricing/PricingPlansView.vue");

// Auth
const AuthSignIn = () => import("@/views/auth/LogInView.vue");
const AuthSignUp = () => import("@/views/auth/SignUpView.vue");

const PasswordResetView = () => import("@/views/auth/PasswordResetView.vue");
const PrivacyPolicyView = () => import("@/views/starter/PrivacyPolicy.vue");
const TOSView = () => import("@/views/starter/TOS.vue");
const RefundsView = () => import("@/views/starter/Refunds.vue");
const PasswordResetConfirmView = () => import("@/views/auth/PasswordResetConfirmView.vue");


// Set all routes
const routes = [
    {
        path: "/",
        component: LayoutLanding,
        children: [
            {
                path: "",
                name: "landing",
                component: LandingHome,
                meta: {disableAuth: true, title: 'PageWatch - Protect Your Imporant Pages'},
                children: [
                    {
                        path: ":reportRoot/:websiteId",
                        name: "demoReport",
                        component: LandingPreview,
                        children: [],
                        meta: {disableAuth: false},
                    },
                ]
            },
            {
                path: "login",
                name: "auth-login",
                component: AuthSignIn,
                meta: {disableAuth: true, hideNav: true, title: 'Login - PageWatch'},
            },
            {
                path: "signup",
                name: "auth-signup",
                component: AuthSignUp,
                meta: {disableAuth: true, hideNav: true, title: 'Sign Up - PageWatch'},
            },
            {
                path: "graphs",
                name: "graphs",
                component: () => import("@/views/starter/Graphs.vue"),
                meta: {disableAuth: true, hideNav: true, title: 'Graphs - PageWatch'},
            },
            {
                path: "pw-reset",
                name: "auth-reminder",
                component: PasswordResetView,
                meta: {disableAuth: true, hideNav: true, title: 'Password Reset - PageWatch'},
            },
            {
                path: "privacy",
                name: "privacy-policy",
                component: PrivacyPolicyView,
                meta: {disableAuth: true, title: 'Privacy Policy - PageWatch'},
            },
            {
                path: "tos",
                name: "tos",
                component: TOSView,
                meta: {disableAuth: true, title: 'Terms Of Service - PageWatch'},
            },
            {
                path: "refunds",
                name: "refunds",
                component: RefundsView,
                meta: {disableAuth: true, title: 'Refunds - PageWatch'},
            },
            {
                path: "pw-reset/confirm/",
                name: "password-reset-confirm",
                component: PasswordResetConfirmView,
                meta: {disableAuth: true, hideNav: true, title: 'Password Reset - PageWatch'},
            },
            {
              path: '/oauth/authorize',
              name: 'oauth-authorize',
              component: () => import('@/views/auth/OAuthAuthorize.vue'),
              meta: { requiresAuth: true },
            },
        ],
    },
    {
        path: "/account",
        component: LayoutActiveWebsite,
        children: [
            {
                path: "dashboard",
                name: "accountDashboard",
                component: () => import("@/views/account/DashboardView.vue"),
                meta: {sidebar: 'user', title: 'Account Dashboard - PageWatch'},
            },
            {
                path: "subscription",
                name: "subscription",
                component: () => import("@/views/account/Subscription.vue"),
                meta: {sidebar: 'user', title: 'Account Subscription - PageWatch'},
            },
            {
                path: "keys",
                name: "keys",
                component: () => import("@/views/account/ApiKeys.vue"),
                meta: {sidebar: 'user', title: 'API Keys - PageWatch'},
            },
            {
                path: "prepaid",
                name: "prepaid",
                component: () => import("@/views/account/Prepaid.vue"),
                meta: {sidebar: 'user', title: 'Prepaid Credits - PageWatch'},
            },
            {
                path: "invoices",
                name: "invoices",
                component: () => import("@/views/account/Invoices.vue"),
                meta: {sidebar: 'user', title: 'Invoices - PageWatch'},
            },
            {
                path: "new",
                name: "newSite",
                component: () => import("@/views/sites/NewSiteView.vue"),
                meta: {sidebar: 'user', title: 'Add New Site - PageWatch'},
            },
            {
                path: "oauth",
                name: "oauth",
                component: () => import("@/views/sites/NewSiteView.vue"),
                meta: {sidebar: 'user', title: 'Add New Site - PageWatch'},
            },
        ]
    },
    {
        path: "/:reportRoot/:websiteId/mcp",
        component: MCPLayout,
        name: "PreviewOverviewMCP",
        children: [
            {
                path: ":pageId/:deviceId",
                name: "PreviewOverview",
                component: PreviewOverviewMCP,
                meta: {title: 'Dashboard - PageWatch'},
            },
        ],
    },
    {
        path: "/:reportRoot/:websiteId",
        component: LayoutActiveWebsite,
        children: [
            {
                path: "dashboard",
                name: "dashboard",
                component: () => import("@/views/dashboard/DashboardView.vue"),
                meta: {title: 'Dashboard - PageWatch'},
                children: [
                    previewRoutes('dashboard'),
                ]
            },
            {
                path: "explore",
                name: "explore",
                component: () => import("@/views/explore/LandingPagesView.vue"),
                meta: {title: 'Explore - PageWatch'},
                children: [
                    previewRoutes('explore')
                ]
            },
            {
                path: "timeline",
                name: "timeline",
                component: () => import("@/views/explore/Timeline.vue"),
                meta: {title: 'Timeline - PageWatch'},
                children: [
                    previewRoutes('timeline')
                ]
            },
            {
                path: "integrity/summary",
                name: "integrity.summary",
                component: () => import("@/views/integrity/Summary.vue"),
                meta: {title: 'Content Integrity - PageWatch', category: 'integrity.summary'},
                children: [
                    previewRoutes('integrity.summary')
                ]
            },
            {
                path: "spelling/summary",
                name: "spelling.summary",
                component: () => import("@/views/spelling/Summary.vue"),
                meta: {title: 'Spelling/Grammar Checks - PageWatch', category: 'spelling.summary'},
                children: [
                    previewRoutes('spelling.summary')
                ]
            },
            {
                path: "spelling/spelling",
                name: "proofread.text_quality.spelling",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {title: 'Spelling - PageWatch', category: 'proofread.text_quality.spelling'},
                children: [
                    previewRoutes('proofread.text_quality.spelling')
                ]
            },
            {
                path: "spelling/grammar",
                name: "proofread.text_quality.grammar",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {title: 'Grammar - PageWatch', category: 'proofread.text_quality.grammar'},
                children: [
                    previewRoutes('proofread.text_quality.grammar')
                ]
            },
            {
                path: "spelling/tone",
                name: "proofread.text_quality.tone",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {title: 'Grammar - PageWatch', category: 'proofread.text_quality.tone'},
                children: [
                    previewRoutes('proofread.text_quality.tone')
                ]
            },
            {
                path: "spelling/readability",
                name: "proofread.text_quality.readability",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {title: 'Grammar - PageWatch', category: 'proofread.text_quality.readability'},
                children: [
                    previewRoutes('proofread.text_quality.readability')
                ]
            },
            {
                path: "spelling/capitalization",
                name: "proofread.text_quality.capitalization",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {title: 'Capitalization - PageWatch', category: 'proofread.text_quality.capitalization'},
                children: [
                    previewRoutes('proofread.text_quality.capitalization')
                ]
            },
            {
                path: "integrity/visible_error_message",
                name: "proofread.content_errors.visible_error_message",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {
                    title: 'Visible Error Messages - PageWatch',
                    category: 'proofread.content_errors.visible_error_message'
                },
                children: [
                    previewRoutes('proofread.content_errors.visible_error_message')
                ]
            },
            {
                path: "integrity/placeholder_text",
                name: "proofread.content_errors.placeholder_text",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {title: 'Placeholder Text - PageWatch', category: 'proofread.content_errors.placeholder_text'},
                children: [
                    previewRoutes('proofread.content_errors.placeholder_text')
                ]
            },
            {
                path: "integrity/unreplaced_variable",
                name: "proofread.content_errors.unreplaced_variable",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {
                    title: 'Unreplaced Variables - PageWatch',
                    category: 'proofread.content_errors.unreplaced_variable'
                },
                children: [
                    previewRoutes('proofread.content_errors.unreplaced_variable')
                ]
            },
            {
                path: "integrity/missing_referenced_element",
                name: "proofread.content_errors.missing_referenced_element",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {
                    title: 'Missing Referenced Element - PageWatch',
                    category: 'proofread.content_errors.missing_referenced_element'
                },
                children: [
                    previewRoutes('proofread.content_errors.missing_referenced_element')
                ]
            },
            {
                path: "integrity/spam_content",
                name: "proofread.content_errors.spam_content",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {title: 'Spam Content - PageWatch', category: 'proofread.content_errors.spam_content'},
                children: [
                    previewRoutes('proofread.content_errors.spam_content')
                ]
            },

            {
                path: "integrity/contradictory_facts",
                name: "proofread.logic.contradictory_facts",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {title: 'Content Inconsistencies - PageWatch', category: 'proofread.logic.contradictory_facts'},
                children: [
                    previewRoutes('proofread.logic.contradictory_facts')
                ]
            },
            {
                path: "integrity/numerical_inconsistency",
                name: "proofread.logic.numerical_inconsistency",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {
                    title: 'Numerical Inconsistencies - PageWatch',
                    category: 'proofread.logic.numerical_inconsistency'
                },
                children: [
                    previewRoutes('proofread.logic.numerical_inconsistency')
                ]
            },
            {
                path: "integrity/heading_content_mismatch",
                name: "proofread.logic.heading_content_mismatch",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {
                    title: 'Heading Content Mismatch - PageWatch',
                    category: 'proofread.logic.heading_content_mismatch'
                },
                children: [
                    previewRoutes('proofread.logic.heading_content_mismatch')
                ]
            },
            {
                path: "integrity/image_text_conflict",
                name: "proofread.content_errors.image_text_conflict",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {
                    title: 'Image Text Conflict - PageWatch',
                    category: 'proofread.content_errors.image_text_conflict'
                },
                children: [
                    previewRoutes('proofread.content_errors.image_text_conflict')
                ]
            },
            {
                path: "integrity/broken_image",
                name: "proofread.images.broken_image",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {title: 'Broken Image - PageWatch', category: 'proofread.images.broken_image'},
                children: [
                    previewRoutes('proofread.images.broken_image')
                ]
            },
            {
                path: "integrity/layout_flow",
                name: "proofread.images.layout_flow",
                component: () => import("@/views/spelling/GrammarView.vue"),
                meta: {title: 'Layout Flow - PageWatch', category: 'proofread.images.layout_flow'},
                children: [
                    previewRoutes('proofread.images.layout_flow')
                ]
            },
            {
                path: "layout/summary",
                name: "layout.summary",
                component: () => import("@/views/layout_issues/Summary.vue"),
                meta: {title: 'Layout Issues - PageWatch'},
                children: [
                    previewRoutes('layout.summary')
                ]
            },
            {
                path: "layout/text-overlap",
                name: "layout.text_overlap",
                component: () => import("@/views/layout_issues/LayoutIssues.vue"),
                meta: {title: 'Layout - PageWatch'},
                children: [
                    previewRoutes('layout.text_overlap')
                ]
            },
            {
                path: "layout/overlapping-buttons",
                name: "layout.button_overlap",
                component: () => import("@/views/layout_issues/LayoutIssues.vue"),
                meta: {title: 'Layout - PageWatch'},
                children: [
                    previewRoutes('layout.button_overlap')
                ]
            },
            {
                path: "layout/missing-breakpoint",
                name: "layout.narrow",
                component: () => import("@/views/layout_issues/LayoutIssues.vue"),
                meta: {title: 'Layout - PageWatch'},
                children: [
                    previewRoutes('layout.narrow')
                ]
            },
            {
                path: "layout/lsep",
                name: "layout.lsep",
                component: () => import("@/views/layout_issues/LayoutIssues.vue"),
                meta: {title: 'Layout - PageWatch'},
                children: [
                    previewRoutes('layout.lsep')
                ]
            },
            {
                path: "layout/etx",
                name: "layout.etx",
                component: () => import("@/views/layout_issues/LayoutIssues.vue"),
                meta: {title: 'Layout - PageWatch'},
                children: [
                    previewRoutes('layout.etx')
                ]
            },
            {
                path: "layout/unicode",
                name: "layout.unsupported_unicode",
                component: () => import("@/views/layout_issues/LayoutIssues.vue"),
                meta: {title: 'Layout - PageWatch'},
                children: [
                    previewRoutes('layout.unsupported_unicode')
                ]
            },
            {
                path: "layout/horizontal-scrolling",
                name: "layout.horizontal_scrolling",
                component: () => import("@/views/layout_issues/LayoutIssues.vue"),
                meta: {title: 'Layout - PageWatch'},
                children: [
                    previewRoutes('layout.horizontal_scrolling')
                ]
            },
            {
                path: "layout/container-overflow",
                name: "layout.overflowing_container",
                component: () => import("@/views/layout_issues/LayoutIssues.vue"),
                meta: {title: 'Layout - PageWatch'},
                children: [
                    previewRoutes('layout.overflowing_container')
                ]
            },
            {
                path: "layout/outside-window",
                name: "layout.outside_window",
                component: () => import("@/views/layout_issues/LayoutIssues.vue"),
                meta: {title: 'Layout - PageWatch'},
                children: [
                    previewRoutes('layout.outside_window')
                ]
            },
            {
                path: "layout/aspect-ratio",
                name: "layout.aspect_ratio",
                component: () => import("@/views/layout_issues/LayoutIssues.vue"),
                meta: {title: 'Layout - PageWatch'},
                children: [
                    previewRoutes('layout.aspect_ratio')
                ]
            },
            {
                path: "technical/summary",
                name: "technical.summary",
                component: () => import("@/views/technical/Summary.vue"),
                meta: {title: 'Technical Checks - PageWatch', category: 'technical.summary'},
                children: [
                    previewRoutes('technical.summary')
                ]
            },
            {
                path: "technical/broken-images",
                name: "technical.broken_image",
                component: () => import("@/views/technical/BrokenImages.vue"),
                meta: {title: 'Broken Images - PageWatch'},
                children: [
                    previewRoutes('technical.broken_image')
                ]
            },
            {
                path: "technical/broken-links",
                name: "technical.broken_link",
                component: () => import("@/views/technical/BrokenLinks.vue"),
                meta: {title: 'Broken Links - PageWatch'},
                children: [
                    previewRoutes('technical.broken_link')
                ]
            },
            {
                path: "technical/broken-resources",
                name: "technical.broken_resource",
                component: () => import("@/views/technical/BrokenResources.vue"),
                meta: {title: 'Broken Resources - PageWatch'},
                children: [
                    previewRoutes('technical.broken_resource')
                ]
            },
            {
                path: "technical/internal-redirects",
                name: "technical.internal_redirect",
                component: () => import("@/views/technical/InternalRedirects.vue"),
                meta: {title: 'Internal Redirects - PageWatch'},
                children: [
                    previewRoutes('technical.internal_redirect'),
                ]
            },
            {
                path: "technical/missing-favicon",
                name: "technical.missing_favicon",
                component: () => import("@/views/technical/MissingFavicon.vue"),
                meta: {title: 'Missing Favicon - PageWatch'},
                children: [
                    previewRoutes('technical.missing_favicon'),
                ]
            },
            {
                path: "technical/missing-descriptions",
                name: "technical.missing_description",
                component: () => import("@/views/technical/MissingDescriptions.vue"),
                meta: {title: 'Missing Descriptions - PageWatch'},
                children: [
                    previewRoutes('technical.missing_description'),
                ]
            },
            {
                path: "technical/missing-titles",
                name: "technical.missing_title",
                component: () => import("@/views/technical/MissingTitles.vue"),
                meta: {title: 'Missing Titles - PageWatch'},
                children: [
                    previewRoutes('technical.missing_title'),
                ]
            },
            {
                path: "technical/missing-alt-tags",
                name: "technical.missing_alt",
                component: () => import("@/views/technical/MissingImageAltTags.vue"),
                meta: {title: 'Missing Alt Tags - PageWatch'},
                children: [
                    previewRoutes('technical.missing_alt'),
                ]
            },
            {
                path: "technical/console-errors",
                name: "technical.console_error",
                component: () => import("@/views/technical/ConsoleErrors.vue"),
                meta: {title: 'Console Errors - PageWatch'},
                children: [
                    previewRoutes('technical.console_error')
                ]
            },
            {
                path: "settings",
                name: "settings",
                component: () => import("@/views/settings/Settings.vue"),
                meta: {title: 'Settings - PageWatch'},
                children: [
                    previewRoutes('settings')
                ]
            },
            {
                path: "options",
                name: "ignoredIssues",
                component: () => import("@/views/settings/IgnoredIssues.vue"),
                meta: {title: 'Ignored Issues - PageWatch'},
                children: [
                    previewRoutes('options')
                ]
            },
            {
                path: "cross-reference/issues",
                name: "crossReferenceIssues",
                component: () => import("@/views/cross_reference/CrossReferenceIssues.vue"),
                meta: {title: 'Cross Reference Issues - PageWatch'},
                children: [
                    previewRoutes('cross-reference-issues')
                ]
            },
            {
                path: "ai/summary",
                name: "ai.summary",
                component: () => import("@/views/ai/Summary.vue"),
                meta: {title: 'AI Analysis Issues - PageWatch'},
                children: [
                    previewRoutes('ai.summary')
                ]
            },
            {
                path: "ai/prompts",
                name: "ai.prompts",
                component: () => import("@/views/ai/TrackedPrompts.vue"),
                meta: {title: 'Watched Prompts - PageWatch'},
                children: [
                    previewRoutes('ai.prompts')
                ]
            },
        ],
    },
    {
        path: "/backend",
        redirect: "/backend/dashboard",
        component: LayoutSimple,
        children: [
            {
                path: "dashboard",
                name: "backend-dashboard",
                component: BackendDashboard,
            },
            {
                path: "spelling",
                name: "backend-spelling",
                component: BackendSpelling,
            },
            {
                path: "website/:websiteId",
                name: "backend-website",
                component: BackendWebsite,
            },
            {
                path: "logs/:rootId",
                name: "backend-logs",
                component: BackendLogs,
            },
            {
                path: "url-group/:groupId",
                name: "backend-url-group",
                component: BackendUrlGroup,
            },
        ]
    },
    {
        path: '/:pathMatch(.*)*',
        name: "not-found",
        component: () => import("@/views/errors/400View.vue"),
    },
];

const isIframe = window.self !== window.top
// Create Router
const router = createRouter({
    history: isIframe ? createMemoryHistory() : createWebHistory(),
    linkActiveClass: "active",
    linkExactActiveClass: "active",
    scrollBehavior() {
        return {left: 0, top: 0};
    },
    routes,
});

// NProgress
/*eslint-disable no-unused-vars*/
NProgress.configure({showSpinner: false});

router.beforeResolve((to, from, next) => {
    NProgress.start();
    next();
});

router.afterEach((to, from) => {
    NProgress.done();
});
/*eslint-enable no-unused-vars*/

export default router;
